import type { IBatchStore } from './batch-store-types';
import logger from '../../utils/logger';
import { timerKey } from '../../utils/timer-manager/timer-manager';
import { DeviceStatus } from '../../constants/DeviceStatus';
import { toNumber } from '../../utils/numbers';
import {
  BatchAction,
  MaterialPosition,
} from '../../constants/MaterialModels';
import { dischargeDecumScale as _dischargeDecumScale } from './decum-scales/discharge-decum-scale';
import { beltSwitch } from './device-switches/belt-switch';
import { getSecondaryBinFreeFallForAutoTuning } from './free-fall-prediction/secondary-bin-freefall';
import { isFeatureEnabled } from '../../utils/feature-flags';
import { BeltStartMode } from '../../components/DeviceAsDom/Belt';
import { BatchCycleStatus } from '../../constants/BatchCycleStatus';
import type { IBatchData } from '../../containers/Batch/IBatchData';
import type { IWeighedDataToBatch } from '../../../types/batch/DataToBatch';
import type {
  IBinEntry,
  IMaterial,
} from '../../../types/material/MaterialModels';
import type { IScale } from '../../../types/device/DeviceModels';
import { isRunModeNewYork } from './run-mode';

const FEATURE_AUTO_TUNING = isFeatureEnabled('auto_tuning');

export const startBatchMixin = {

  checkIfFirstMaterialForScaleIsTurboBatch(this: IBatchStore, initialData: IBatchData, scaleId: number) {

    const d = initialData.scales?.[scaleId].length ? initialData.scales[scaleId][0] : null;

    if (!d?.materialDetails || !d.material) {
      return true;
    }

    logger.log(`first material ${d.material.code} for scale #${scaleId} is turbo batch?`, d.materialDetails.turboBatch);

    return !!d.materialDetails.turboBatch;
  },

  showTargetsOnPumps(this: IBatchStore, runningData: IBatchData, index: number = 1): void {

    if (this.running.length === 1 && runningData.pumps) {
      Object.keys(runningData.pumps).forEach(pumpId => {
        let pumpData = runningData.pumps[pumpId];
        logger.log('set pump target for first load:', this.materialStore.getMaterialName(pumpData.material), pumpData.mft, runningData.uid);

        this.deviceStatus.set(Number(pumpId), {
          ...this.getDeviceStatusDefault(Number(pumpId)),
          id: Number(pumpId),
          status: pumpData.headWaterStarted ? this.getDeviceStatus(Number(pumpId)).status : DeviceStatus.IDLE,
          target: pumpData.mft,
          currentDrop: runningData.currentDrop,
          allDrops: runningData.allDrops,
          uid: runningData.uid,
          tId: runningData.tId,
          truckCode: runningData.truckCode,
        });
      });
    } else if (this.running.length > index) {
      const d0 = this.running[index-1];
      const d = this.running[index];

      if (runningData.pumps && d?.uid === runningData.uid && d.currentDrop === runningData.currentDrop) {
        Object.keys(runningData.pumps).forEach(pumpId => {
          let pumpData = runningData.pumps[pumpId];
          let prevPumpData = d0.pumps?.[pumpId];

          if (prevPumpData?.isDischarged && !pumpData.isDischarged) {
            logger.log('set pump target for next load:', this.materialStore.getMaterialName(pumpData.material), pumpData.mft, runningData.uid);

            this.deviceStatus.set(Number(pumpId), {
              ...this.getDeviceStatusDefault(Number(pumpId)),
              id: Number(pumpId),
              target: pumpData.mft,
              currentDrop: runningData.currentDrop,
              allDrops: runningData.allDrops,
              uid: runningData.uid,
              tId: runningData.tId,
              truckCode: runningData.truckCode,
            });
          }
        });
      }
    }
  },

  setActiveBinByBay(this: IBatchStore, scaleId: number, initialData: IBatchData): void {

    if (!initialData.bay || !initialData.useBay) {
      return;
    }

    const dataForScale = initialData.scales[scaleId];

    if (!dataForScale || !scaleId) {
      return;
    }

    dataForScale.forEach((data: IWeighedDataToBatch): void => {
      const { bin, material, materialDetails } = data;

      if (materialDetails.bins.some((v: IBinEntry) => v.bay === initialData.bay)) {
        logger.log('Set active bin by bay:', material.code, initialData.bay, initialData.useBay);

        materialDetails.bins.forEach((v: IBinEntry, i: number) => {

          if (bin.id === v.binId) {
            materialDetails.bins[i] = { ...v, binId: v.binId, bay: v.bay, isActive: true, isPrimary: true };
          } else {
            materialDetails.bins[i] = { ...v, binId: v.binId, bay: v.bay, isActive: false, isPrimary: false };
          }

        });

        this.materialStore.saveMaterialDetail(material, { ...materialDetails }, true);
      }

    });
  },

  showTargetOnBins(this: IBatchStore, scaleId: number, initialData: IBatchData): void {

    const dataForScale = initialData.scales[scaleId];
    logger.log('Show target on bins for scale:', scaleId, `${initialData.currentDrop}/${initialData.allDrops}`);

    if (!dataForScale || !scaleId) {
      logger.warn('Can not batch, no dataForScale', dataForScale, scaleId);
      return;
    }

    let target = 0;
    dataForScale.forEach((data: IWeighedDataToBatch): void => {
      const { unconvertedTarget, materialDetails } = data;
      const bins = this.getBins(data);
      const primaryBin = bins.primaryBin;
      const secondaryBin = bins.secondaryBin;
      target += toNumber(unconvertedTarget);
      const primaryTarget = toNumber(
        isRunModeNewYork(initialData.runMode)
          ? target
          : unconvertedTarget,
      );

      if (primaryBin) {
        this.deviceStatus.set(primaryBin.id, {
          ...this.getDeviceStatusDefault(primaryBin.id),
          id: primaryBin.id,
          target: primaryTarget,
        });
      }

      if (secondaryBin) {
        let secondaryFreeFall = secondaryBin.freeFall || 0;
        if (FEATURE_AUTO_TUNING && primaryBin.autoTuning) {
          const secondaryFreeFallForAutoTuning = getSecondaryBinFreeFallForAutoTuning(materialDetails.type);
          if (primaryTarget > secondaryFreeFallForAutoTuning) {
            secondaryFreeFall = secondaryFreeFallForAutoTuning;
          }
        }

        this.deviceStatus.set(secondaryBin.id, {
          ...this.getDeviceStatusDefault(secondaryBin.id),
          id: secondaryBin.id,
          target: toNumber(primaryTarget - secondaryFreeFall),
        });
      }
    });
  },

  batchDataForScale(this: IBatchStore, scaleId: number, initialData: IBatchData): void {

    const dataForScale = initialData.scales[scaleId];
    logger.log('batchDataForScale: scale:', scaleId, `${initialData.currentDrop}/${initialData.allDrops}`);

    if (!dataForScale || !scaleId || !this.isActiveDrop(initialData)) {
      logger.warn('Can not batch, no dataForScale or not active drop', dataForScale, scaleId);
      return;
    }

    dataForScale.forEach((data: IWeighedDataToBatch, index: number) => {
      const {
        bin,
        scale,
        material,
        isFeedFinish,
        overcapacityTarget,
        isFeedFinishHeadWater,
        isDischargeFinishHeadWater,
      } = data;

      if (bin) {
        if (overcapacityTarget && isFeedFinishHeadWater && !isDischargeFinishHeadWater) {
          this.goToDischarge(data, initialData);
        } else if ((index === 0 && !isFeedFinish) || (index > 0 && !isFeedFinish && dataForScale[index - 1].isFeedFinish)) {
          this.batchMaterial(data, initialData);
        } else if (index === dataForScale.length - 1 && isFeedFinish) {
          this.goToDischarge(data, initialData);
        }
      } else if (scale.decumulative && this.isScaleReadyForBatch(scale.id, initialData) ) {
        const scaleStatus = this.getDeviceStatus(scale.id);
        if (scaleStatus.status === DeviceStatus.IDLE || scaleStatus.status === DeviceStatus.FINISH) {
          const s = this.getDeviceStatus(`${scale.id}-${material.code}`);
          if (!isFeedFinish && (s.status === DeviceStatus.IDLE || scaleStatus.status === DeviceStatus.FINISH)) {
            this.goToDischarge(data, initialData);
            this.checkAddingWeightToDecumScale(data, initialData);
          }
        }
      }
    });

  },

  isScaleReadyForDischargeToNextScale(this: IBatchStore, data: IWeighedDataToBatch, initialData: IBatchData): boolean {

    const  { scale } = data;

    if (initialData.materialId || !scale.dischargeAfterScaleId || !scale.dischargeAfterScaleDelay) {
      return true;
    }

    const otherScale = this.devicesStore.getDeviceById(scale.dischargeAfterScaleId);

    if (!otherScale) {
      logger.warn('No scale found with dischargeAfterScaleId', scale.dischargeAfterScaleId);
      return true;
    }

    const s = this.getDeviceStatus(scale.dischargeAfterScaleId);
    const o = initialData.scales[scale.dischargeAfterScaleId];

    if (!o) {
      logger.log(scale.description, `no waiting for scale ${otherScale.description}, not used in the mix`);
      return true;
    }

    const otherScaleFinishFeed = o && o.length === o.filter((v: IWeighedDataToBatch) => v.isFeedFinish).length;

    if (s && (s.status === DeviceStatus.DISCHARGING || (s.status === DeviceStatus.IDLE && otherScaleFinishFeed))) {
      return true;
    }

    logger.log(scale.description, 'waiting for scale to start discharge after ', scale.dischargeAfterScaleId, otherScale.description);
    return false;
  },

  isPrevLoadHasFinishDischargeToNextScale(this: IBatchStore, nextScale: IScale, initialData: IBatchData) {

    if (this.running.length === 1) {
      return true;
    }

    if (this.running[0].uid === initialData.uid && this.running[0].currentDrop === initialData.currentDrop) {
      return true;
    }

    const index = this.running.findIndex(v => v.uid === initialData.uid && v.currentDrop === initialData.currentDrop);

    let hasFinish = true;
    this.running.forEach((v, i) => {
      if (i === index - 1) {
        Object.keys(v.scales).forEach(scaleId => {
          const s = this.getDeviceStatus(Number(scaleId));
          const scale = this.devicesStore.getDeviceById(Number(scaleId));

          if (scale?.parentScaleId === nextScale.id
            && v.uid === s.uid && v.currentDrop === s.currentDrop
            && s.status !== DeviceStatus.IDLE && s.status !== DeviceStatus.FINISH) {

            logger.log('prev load scale hasn\'t finish', v.tId, scale.description, nextScale.description, s.status);
            hasFinish = false;
          }
        });
      }
    });

    return hasFinish;
  },

  allUpperScalesAreReadyForDischarge(this: IBatchStore, nextScaleId: number, initialData: IBatchData) {

    const scales = this.devicesStore.scales.filter((v: IScale) => v.parentScaleId === nextScaleId);
    let isReady = true;

    scales.forEach((scale: IScale) => {
      const s = this.getDeviceStatus(scale.id);

      if (
        (!scale.decumulative && initialData.scales[scale.id]
          && initialData.scales[scale.id].length !== initialData.scales[scale.id].filter((v: IWeighedDataToBatch) => v.isFeedFinish).length
        )
        || [DeviceStatus.NOT_EMPTY].includes(s.status)
        || (initialData.uid === s.uid && initialData.currentDrop === s.currentDrop
        && ![DeviceStatus.WAIT_DISCH, DeviceStatus.DELAY, DeviceStatus.DISCHARGING, DeviceStatus.FINISH].includes(s.status))
      ) {
        logger.log(`scale ${scale.description} is not ready: dischargeUpperScalesTogether`, s.status);
        isReady = false;
      }
    });

    return isReady;
  },

  isNextScaleReadyForFeed(this: IBatchStore, data: IWeighedDataToBatch, initialData: IBatchData): boolean {

    const  { scale, material } = data;
    const nextScale = this.devicesStore.getDeviceById(scale.parentScaleId);
    const weightNext = this.getScaleWeight(nextScale);
    const nextScaleStatus = this.getDeviceStatus(nextScale.id);
    const isTheSameDataInNextScale = this.isTheSameDataInNextScale(nextScale, initialData);

    if (nextScaleStatus.status !== DeviceStatus.IDLE && nextScaleStatus.status !== DeviceStatus.FEED
      && nextScaleStatus.status !== DeviceStatus.FINISH && nextScaleStatus.status !== DeviceStatus.NOT_EMPTY
      && nextScaleStatus.status !== DeviceStatus.OPEN_ERROR) {
      logger.log(`Can't discharge to next scale: scale #${nextScale.id} status: ${nextScaleStatus.status}!`, material.code);
      return false;
    }

    if (!isTheSameDataInNextScale && (nextScaleStatus.status === DeviceStatus.WAIT_DISCH || nextScaleStatus.status === DeviceStatus.FEED)) {
      logger.log(`Can't discharge to next scale: scale #${nextScale.id} is not the same data!`, material.code, nextScaleStatus.status);
      return false;
    }

    if (nextScaleStatus.status === DeviceStatus.FINISH) {
      const d = this.running.find(v => v.uid === nextScaleStatus.uid && v.currentDrop === nextScaleStatus.currentDrop);
      if (d?.finalScales[nextScale.id] && !d.finalScalesDischarged.includes(nextScale.id)) {
        logger.log(`Can't discharge to next scale: scale #${nextScale.id} still finishing discharge!`, material.code, nextScaleStatus.status);
        return false;
      }
    }

    if (!this.isPrevLoadHasFinishDischargeToNextScale(nextScale, initialData)) {
      logger.log(`Can't discharge to next scale: scale #${nextScale.id} waiting on prev load!`, material.code, nextScaleStatus.status);
      return false;
    }

    if (nextScale.dischargeUpperScalesTogether) {
      if (!this.allUpperScalesAreReadyForDischarge(nextScale.id, initialData)) {
        logger.log(`Can't discharge to next scale: ${nextScale.description} waiting on upper scales!`, material.code, scale.description);
        return false;
      }
    }

    if (weightNext > (scale.startTare || 0) && !this.isOverride && !isTheSameDataInNextScale) {
      logger.log(`Can't discharge to next scale: scale #${nextScale.id} is not empty! weight: ${weightNext}`, material.code, nextScaleStatus.status);
      this.deviceStatus.set(nextScale.id, {
        ...nextScaleStatus,
        weight: weightNext,
        status: DeviceStatus.NOT_EMPTY,
        error: true,
      });

      return false;
    }

    if (this.isScaleOpen(nextScale)) {
      logger.log(`Scale #${nextScale.id} gate is open! Closing ... `);
      this.deviceStatus.set(nextScale.id, {
        ...nextScaleStatus,
        weight: weightNext,
        status: DeviceStatus.OPEN_ERROR,
        error: true,
      });
      this.closeScale(nextScale);

      return false;
    }

    const belt = this.devicesStore.getDeviceById(scale.beltId);
    if (belt && !this.isOutputOn(belt.onPNo)) {
      logger.log(`Belt #${belt.id} is OFF, turning ON`, scale.id);
      beltSwitch(this.rootStore, scale.beltId);

      return false;
    }

    if ((nextScaleStatus.overridden && nextScaleStatus.status === DeviceStatus.NOT_EMPTY)
      || (nextScaleStatus.status === DeviceStatus.OPEN_ERROR && !this.isScaleOpen(nextScale))) {
      this.deviceStatus.set(nextScale.id, {
        ...nextScaleStatus,
        weight: weightNext,
        status: DeviceStatus.IDLE,
        error: false,
      });
    }

    return true;
  },

  goToDischarge(this: IBatchStore, data: IWeighedDataToBatch, initialData: IBatchData): void {

    const { uid, currentDrop, truckCode } = initialData;

    if (!this.isActiveDrop(initialData)) {
      return;
    }

    const { scale, material, isFeedFinish } = data;
    const scaleStatus = this.getDeviceStatus(scale.id);

    if (scale.decumulative) {

      if (isFeedFinish) {
        return;
      }

      if (material.scaleId && !material.visible) {
        this.materialStore.switchMaterialVisibilityForDecumScale(material);
      }

      if (scaleStatus.status === DeviceStatus.IDLE || scaleStatus.status === DeviceStatus.FINISH) {
        const sid = `${scale.id}-${material.code}`;
        let s = this.getDeviceStatus(sid);
        if (!isFeedFinish && (s.status === DeviceStatus.IDLE || s.status === DeviceStatus.FINISH)) {
          this.deviceStatus.set(sid, {
            ...this.getDeviceStatusDefault(scale),
            id: sid,
          });
          this.setTargetsForDecumScale(data, initialData);
        }
      }

      if (this.isAbort || !this.isActiveDrop(initialData)) {
        return;
      }

      this.onBatchAction(initialData, BatchAction.NoWait);
      this.onBatchAction(initialData, BatchAction.StartBatching);

      if (scale.parentScaleId) {
        if (this.isNextScaleReadyForFeed(data, initialData)) {
          this.dischargeDecumScale(data, initialData);
        } else {
          this.timers.set(timerKey.goToDischarge(), () => {
            this.goToDischarge(data, initialData);
          }, 2000);
        }

      } else if (this.discharging) {
        this.dischargeDecumScale(data, initialData);
      } else {
        const runningData = { scaleId: scale.id, uid, currentDrop };
        if (!this.waitingForDischarge.some((v: any) => v.scaleId === scale.id && v.uid === uid)) {
          this.waitingForDischarge.push(runningData);
        }

        if (this.isDischarge && !this.discharging) {
          this.timers.set(timerKey.dischargeToNextScaleTimer(), () => {
            if (this.isDischarge && !this.discharging) {
              this.discharge();
            }
          }, 1000);
        }
      }

      return;

    }

    if (scaleStatus.status !== DeviceStatus.WAIT_DISCH) {
      this.deviceStatus.set(scale.id, {
        ...scaleStatus,
        uid,
        currentDrop,
        truckCode,
        allDrops: initialData.allDrops,
        weight: this.getScaleWeight(data.scale),
        status: DeviceStatus.WAIT_DISCH,
      });
    }

    if (scale.parentScaleId) {
      return this.dischargeToNextScale(data, scale.parentScaleId, uid, initialData);
    }

    if (scale.beltId) {
      const belt = this.devicesStore.getDeviceById(scale.beltId);
      if (belt?.startMode === BeltStartMode.FinishedWeighting) {
        beltSwitch(this.rootStore, data.scale.beltId);
      }
    }

    const runningData = { scaleId: scale.id, uid, tId: initialData.tId, currentDrop };
    if (!this.waitingForDischarge.some(v => v.scaleId === scale.id && v.uid === uid)) {
      this.waitingForDischarge.push(runningData);
    }

    if (!this.isDischarge && this.isReadyForFinalDischarge(initialData)) {
      this.showReadyForDischarge(initialData);
      this.context.onBatchCycle(BatchCycleStatus.LOAD_READY_FOR_DISCHARGE, this.inputs, this.outputs, this.channels);
    }

    if (this.isDischarge) {
      this.discharge();
    }
  },

  getScalePorts(this: IBatchStore, scale: IScale, material: IMaterial) {

    if (!scale) {
      logger.error('No scale for getScalePorts', scale);
      return {};
    }

    if (scale.decumulative) {
      let openPort, closePort, statusPort, openPortAdditional, closePortAdditional;

      if (material?.position === MaterialPosition.One) {
        openPort = scale.openGateOnePNo;
        closePort = scale.closeGateOnePNo;
        statusPort = scale.gateOneStatusPNo;
        openPortAdditional = scale.openGateOneAdditionalPNo;
        closePortAdditional = scale.closeGateOneAdditionalPNo;
      } else if (material?.position === MaterialPosition.Two) {
        openPort = scale.openGateTwoPNo;
        closePort = scale.closeGateTwoPNo;
        statusPort = scale.gateTwoStatusPNo;
        openPortAdditional = scale.openGateTwoAdditionalPNo;
        closePortAdditional = scale.closeGateTwoAdditionalPNo;
      } else if (material?.position === MaterialPosition.Three) {
        openPort = scale.openGateThreePNo;
        closePort = scale.closeGateThreePNo;
        statusPort = scale.gateThreeStatusPNo;
        openPortAdditional = scale.openGateThreeAdditionalPNo;
        closePortAdditional = scale.closeGateThreeAdditionalPNo;
      } else {
        logger.warn(`No material position set for DECUM scale, ${scale.description}`, material?.code);
      }

      return {
        openPort,
        closePort,
        statusPort,
        openPortAdditional,
        closePortAdditional,
      };
    }

    return {
      openPort: scale.openGateOnePNo,
      closePort: scale.closeGateOnePNo,
      statusPort: scale.gateOneStatusPNo,
    };
  },

  setTargetsForDecumScale(
    this: IBatchStore,
    data: IWeighedDataToBatch,
    initialData: IBatchData,
    status: string = DeviceStatus.WAIT_DISCH,
    isAdd: boolean = false,
  ) {

    const { scale, material, materialDetails } = data;
    const scaleId = scale.id;

    if (!initialData.scales || !scaleId || !initialData.scales[scaleId]) {
      return;
    }

    const { uid, tId, currentDrop, allDrops, truckCode } = initialData;
    const weight = this.getScaleWeight(scale);
    let decumTarget = 0;
    let holdWeight = 0;

    initialData.scales[scaleId].forEach((v: IWeighedDataToBatch) => {
      const sid = `${v.scale.id}-${v.material.code}`;
      const sm = this.getDeviceStatus(sid);
      if (sm.status !== DeviceStatus.FINISH) {
        this.deviceStatus.set(sid, {
          ...sm,
          uid,
          tId,
          truckCode,
          currentDrop,
          allDrops,
          unit: materialDetails.itemBatchUnitSymbol,
          status: v.material.code === material.code ? status : sm.status,
          target: v.mft,
          startWeight: sm.holdWeight ? weight - sm.holdWeight : sm.startWeight || 0,
          finishTarget: sm.holdWeight ? weight - sm.holdWeight - v.mft : weight - v.mft,
        });

        decumTarget += v.mft;
        holdWeight += sm.holdWeight || 0;
      }
    });

    const s = this.getDeviceStatus(scaleId);
    this.deviceStatus.set(scaleId, {
      ...s,
      uid,
      tId,
      truckCode,
      currentDrop,
      allDrops,
      status,
      unit: materialDetails.itemBatchUnitSymbol,
      startWeight: holdWeight ? weight - holdWeight : isAdd ? weight : s.startWeight || weight,
      totalFinalTarget: (weight - holdWeight) - decumTarget,
    });
  },

  dischargeDecumScale(this: IBatchStore, data: IWeighedDataToBatch, initialData: IBatchData, scaleRef: any = {}) {
    return _dischargeDecumScale(this, data, initialData, scaleRef);
  },

};
